import { watch } from 'node:fs';
import { resolve } from 'node:path';
import chalk from 'chalk';
import { loadConfig, getDefaultConfig } from '../../engine/config-loader.js';
import { runBuiltinChecks } from '../../tools/builtin.js';
import type { CheckResult } from '../../types/index.js';

interface WatchOptions {
  debounce?: number;
}

export async function watchCommand(options: WatchOptions): Promise<void> {
  const cwd = process.cwd();
  const debounceMs = options.debounce ?? 500;

  console.log(chalk.bold.cyan('\n🛤️  Devrail Guard Mode\n'));
  console.log(chalk.dim('Watching for changes... Press Ctrl+C to stop.\n'));

  let lastRun = 0;
  let timeout: NodeJS.Timeout | null = null;

  const runCheck = async () => {
    const now = Date.now();
    if (now - lastRun < debounceMs) return;
    lastRun = now;

    console.clear();
    console.log(chalk.bold.cyan('🛤️  Devrail Guard Mode'));
    console.log(chalk.dim(`Last check: ${new Date().toLocaleTimeString()}\n`));

    try {
      const results = await runBuiltinChecks(cwd, [
        'secrets.gitignore-required',
        'secrets.no-env-commit',
        'deps.lockfile.required',
        'tests.unit.required',
        'code.strict-mode',
      ]);

      const errors = results.filter((r) => r.severity === 'error');
      const warnings = results.filter((r) => r.severity === 'warn');

      if (results.length === 0) {
        console.log(chalk.green.bold('✓ All clear!\n'));
      } else {
        console.log(chalk.bold('Issues:'));
        
        for (const result of errors) {
          console.log(`  ${chalk.red('✖')} ${result.message}`);
          console.log(`    ${chalk.dim(result.ruleId)}`);
        }
        
        for (const result of warnings.slice(0, 5)) {
          console.log(`  ${chalk.yellow('⚠')} ${result.message}`);
          console.log(`    ${chalk.dim(result.ruleId)}`);
        }

        if (warnings.length > 5) {
          console.log(chalk.dim(`\n  ... and ${warnings.length - 5} more warnings`));
        }

        console.log(`\n${chalk.red('Errors:')} ${errors.length} | ${chalk.yellow('Warnings:')} ${warnings.length}`);
      }

      // Quick score
      const score = Math.max(0, 100 - errors.length * 10 - warnings.length * 3);
      const scoreColor = score >= 80 ? chalk.green : score >= 50 ? chalk.yellow : chalk.red;
      console.log(`\nScore: ${scoreColor.bold(score + '/100')}`);

      console.log(chalk.dim('\nWatching for changes...'));
    } catch (error) {
      console.error(chalk.red('Check failed:'), error);
    }
  };

  // Initial run
  await runCheck();

  // Watch for changes
  const watcher = watch(cwd, { recursive: true }, (eventType, filename) => {
    if (!filename) return;
    
    // Ignore node_modules, dist, .git
    if (
      filename.includes('node_modules') ||
      filename.includes('dist') ||
      filename.includes('.git') ||
      filename.includes('.devrail')
    ) {
      return;
    }

    // Debounce
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(runCheck, debounceMs);
  });

  // Handle exit
  process.on('SIGINT', () => {
    watcher.close();
    console.log(chalk.dim('\n\nStopped watching.\n'));
    process.exit(0);
  });
}
